import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumChangeSection implements BaseEnumType {
  private static allValues: EnumChangeSection[] = []; // make sure this is top-most
  
  public static general = new EnumChangeSection(1, "General", "enum_change_section_general");
  public static orders = new EnumChangeSection(2, "Orders", "enum_change_section_orders");
  public static credits = new EnumChangeSection(3, "Credits", "enum_change_section_credits");
  public static externalOrders = new EnumChangeSection(4, "ExternalOrders", "enum_change_section_external_orders");
  public static containerTracking = new EnumChangeSection(5, "ContainerTracking", "enum_change_section_container_tracking");
  public static ullage = new EnumChangeSection(6, "Ullage", "enum_change_section_ullage");
  public static duty = new EnumChangeSection(7, "Duty", "enum_change_section_duty");
  public static reporting = new EnumChangeSection(8, "Reporting", "enum_change_section_reporting");
  public static security = new EnumChangeSection(9, "Security", "enum_change_section_security");
  public static newVersion = new EnumChangeSection(10, "NewVersion", "enum_change_section_new_version");
  public static brewRep = new EnumChangeSection(11, "BrewRep", "enum_change_section_brew_rep");
  public static stock = new EnumChangeSection(12, "Stock", "enum_change_section_stock");
  public static outlets = new EnumChangeSection(13, "Outlets", "enum_change_section_outlets");
  public static telesales = new EnumChangeSection(14, "Telesales", "enum_change_section_telesales");
  public static prices = new EnumChangeSection(15, "Prices", "enum_change_section_prices");
  public static production = new EnumChangeSection(16, "Production", "enum_change_section_production");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumChangeSection.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isGeneral() { return this === EnumChangeSection.general; }
  public isOrders() { return this === EnumChangeSection.orders; }
  public isCredits() { return this === EnumChangeSection.credits; }
  public isExternalOrders() { return this === EnumChangeSection.externalOrders; }
  public isContainerTracking() { return this === EnumChangeSection.containerTracking; }
  public isUllage() { return this === EnumChangeSection.ullage; }
  public isDuty() { return this === EnumChangeSection.duty; }
  public isReporting() { return this === EnumChangeSection.reporting; }
  public isSecurity() { return this === EnumChangeSection.security; }
  public isNewVersion() { return this === EnumChangeSection.newVersion; }
  public isBrewRep() { return this === EnumChangeSection.brewRep; }
  public isStock() { return this === EnumChangeSection.stock; }
  public isOutlets() { return this === EnumChangeSection.outlets; }
  public isTelesales() { return this === EnumChangeSection.telesales; }
  public isPrices() { return this === EnumChangeSection.prices; }
  public isProduction() { return this === EnumChangeSection.production; }

  public static getValues(): readonly EnumChangeSection[] {
    return EnumChangeSection.allValues;
  }

  public static getValuesExcept0(): readonly EnumChangeSection[] {
    return EnumChangeSection.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumChangeSection {
    const found = EnumChangeSection.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumChangeSection - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumChangeSection): EnumChangeSection {
    const found = name ? EnumChangeSection.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumChangeSection - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumChangeSection>): boolean {
    return this.key === other?.key;
  }
}